import { useEffect, useMemo, useRef } from 'react';
import type { Provedor } from './tipos';

/** Origem do iframe de cada provedor; só mensagens dessa origem são aceitas. */
function origemDoPlayer(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

/** Endereço do iframe com os parâmetros que cada provedor aceita para retomar e informar a posição. */
function urlFinal(provedor: Provedor, base: string, inicio: number): string {
  const origem = typeof window === 'undefined' ? '' : window.location.origin;
  if (provedor === 'youtube') {
    const q = new URLSearchParams({ enablejsapi: '1', rel: '0', origin: origem });
    if (inicio > 5) q.set('start', String(Math.floor(inicio)));
    return `${base}?${q.toString()}`;
  }
  if (provedor === 'vimeo') return inicio > 5 ? `${base}#t=${Math.floor(inicio)}s` : base;
  return base;
}

interface Props {
  provedor: Provedor;
  /** Endereço do player montado pelo servidor a partir do provedor e do id. */
  url: string;
  titulo: string;
  /** Posição salva em segundos; o player retoma dela quando o provedor aceita. */
  inicio: number;
  /** Chamado com a posição atual (no máximo a cada 15 s, quando ela muda) para salvar o progresso. */
  aoPosicao?: (segundos: number) => void;
  /** O vídeo chegou ao fim. */
  aoTerminar?: () => void;
}

/**
 * M11.2: player externo por iframe. YouTube, Vimeo e Bunny informam a posição por postMessage
 * (sem carregar script de terceiros); Panda toca sem rastrear a posição.
 */
export function Player({ provedor, url, titulo, inicio, aoPosicao, aoTerminar }: Props) {
  const ref = useRef<HTMLIFrameElement>(null);
  const ultimo = useRef({ seg: inicio, em: 0 });
  const callbacks = useRef({ aoPosicao, aoTerminar });
  callbacks.current = { aoPosicao, aoTerminar };
  // O endereço inicial fica fixo durante a aula: mudar `start` recarregaria o vídeo a cada salvamento.
  const src = useMemo(() => urlFinal(provedor, url, inicio), [provedor, url]); // eslint-disable-line react-hooks/exhaustive-deps
  const origem = origemDoPlayer(url);

  useEffect(() => {
    ultimo.current = { seg: inicio, em: Date.now() };
    const registrar = (seg: number) => {
      if (!Number.isFinite(seg) || seg < 0) return;
      const agora = Date.now();
      if (agora - ultimo.current.em < 15_000 || Math.abs(seg - ultimo.current.seg) < 5) return;
      ultimo.current = { seg, em: agora };
      callbacks.current.aoPosicao?.(Math.floor(seg));
    };
    const ouvir = (e: MessageEvent) => {
      if (e.origin !== origem || e.source !== ref.current?.contentWindow) return;
      let d: unknown = e.data;
      if (typeof d === 'string') {
        try {
          d = JSON.parse(d);
        } catch {
          return;
        }
      }
      const m = d as { event?: string; info?: { currentTime?: number; playerState?: number } | number; data?: { seconds?: number }; value?: { seconds?: number } };
      if (provedor === 'youtube') {
        if (m.event === 'infoDelivery' && typeof m.info === 'object' && typeof m.info.currentTime === 'number') registrar(m.info.currentTime);
        if ((m.event === 'onStateChange' && m.info === 0) || (m.event === 'infoDelivery' && typeof m.info === 'object' && m.info.playerState === 0)) callbacks.current.aoTerminar?.();
      } else if (provedor === 'vimeo') {
        if (m.event === 'timeupdate' && typeof m.data?.seconds === 'number') registrar(m.data.seconds);
        if (m.event === 'ended') callbacks.current.aoTerminar?.();
      } else if (provedor === 'bunny') {
        if (m.event === 'timeupdate' && typeof m.value?.seconds === 'number') registrar(m.value.seconds);
        if (m.event === 'ended') callbacks.current.aoTerminar?.();
      }
    };
    window.addEventListener('message', ouvir);
    return () => window.removeEventListener('message', ouvir);
  }, [provedor, origem]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Depois de carregar, pede ao player que comece a informar a posição. */
  function aoCarregar() {
    const alvo = ref.current?.contentWindow;
    if (!alvo || !origem) return;
    if (provedor === 'youtube') alvo.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), origem);
    if (provedor === 'vimeo') {
      for (const evento of ['timeupdate', 'ended']) alvo.postMessage(JSON.stringify({ method: 'addEventListener', value: evento }), origem);
    }
    if (provedor === 'bunny') {
      for (const evento of ['timeupdate', 'ended']) alvo.postMessage(JSON.stringify({ context: 'player.js', version: '0.0.11', method: 'addEventListener', value: evento }), origem);
    }
  }

  return (
    <iframe
      ref={ref}
      key={src}
      src={src}
      title={titulo}
      onLoad={aoCarregar}
      loading="lazy"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope"
      referrerPolicy="strict-origin-when-cross-origin"
      className="absolute inset-0 size-full border-0"
    />
  );
}
